import { useTranslation } from 'react-i18next';

import { Button, MarkdownEditor } from '@/shared/ui';

import { type DraftBlock, moveBlock, newChecklistBlock, newMarkdownBlock } from './draft';
import styles from './AdvancedCreate.module.css';

interface Props {
  blocks: readonly DraftBlock[];
  onChange: (blocks: DraftBlock[]) => void;
}

const MARKDOWN_MAX = 50_000;
const ITEM_MAX = 1000;

/**
 * The content blocks of the card being composed: Markdown text and checklists in any order.
 * Same vocabulary as the card dialog, but local: nothing is saved until the whole card is
 * created.
 */
export function DraftContent({ blocks, onChange }: Props) {
  const { t } = useTranslation();
  const update = (key: string, patch: Partial<DraftBlock>) => {
    onChange(
      blocks.map((block) => (block.key === key ? ({ ...block, ...patch } as DraftBlock) : block)),
    );
  };

  return (
    <section className={styles.section} aria-labelledby="draft-content-title">
      <h3 id="draft-content-title" className={styles.sectionTitle}>
        {t('content.title')}
      </h3>
      {blocks.length === 0 && <p className={styles.hint}>{t('content.empty')}</p>}
      <ol className={styles.blocks}>
        {blocks.map((block, index) => (
          <li key={block.key} className={styles.block} data-testid="draft-block">
            <div className={styles.blockBody}>
              {block.kind === 'markdown' ? (
                <MarkdownEditor
                  label={t('content.markdown')}
                  value={block.text}
                  maxLength={MARKDOWN_MAX}
                  rows={5}
                  onChange={(text) => {
                    update(block.key, { text });
                  }}
                />
              ) : (
                <div className={styles.checklist}>
                  <input
                    className={styles.input}
                    aria-label={t('checklist.titleLabel')}
                    placeholder={t('checklist.untitled')}
                    value={block.title}
                    maxLength={200}
                    onChange={(event) => {
                      update(block.key, { title: event.target.value });
                    }}
                  />
                  <ul className={styles.items}>
                    {block.items.map((item, itemIndex) => (
                      <li key={itemIndex} className={styles.item}>
                        <input
                          className={styles.input}
                          aria-label={t('checklist.itemText')}
                          value={item}
                          maxLength={ITEM_MAX}
                          onChange={(event) => {
                            update(block.key, {
                              items: block.items.map((value, i) =>
                                i === itemIndex ? event.target.value : value,
                              ),
                            });
                          }}
                        />
                        <button
                          type="button"
                          className={styles.tool}
                          aria-label={t('checklist.deleteItem', { text: item })}
                          onClick={() => {
                            update(block.key, {
                              items: block.items.filter((_, i) => i !== itemIndex),
                            });
                          }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                  <input
                    className={styles.input}
                    aria-label={t('checklist.addItem')}
                    placeholder={t('checklist.addItem')}
                    maxLength={ITEM_MAX}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter') return;
                      event.preventDefault(); // Enter adds the item, it does not submit the card
                      const input = event.currentTarget;
                      const text = input.value.trim();
                      if (!text) return;
                      update(block.key, { items: [...block.items, text] });
                      input.value = '';
                    }}
                  />
                </div>
              )}
            </div>
            <div className={styles.blockTools} role="group" aria-label={t('content.blockActions')}>
              <button
                type="button"
                className={styles.tool}
                aria-label={t('content.moveUp')}
                disabled={index === 0}
                onClick={() => {
                  onChange(moveBlock(blocks, block.key, -1));
                }}
              >
                ↑
              </button>
              <button
                type="button"
                className={styles.tool}
                aria-label={t('content.moveDown')}
                disabled={index === blocks.length - 1}
                onClick={() => {
                  onChange(moveBlock(blocks, block.key, 1));
                }}
              >
                ↓
              </button>
              <button
                type="button"
                className={styles.tool}
                aria-label={t('content.delete')}
                onClick={() => {
                  onChange(blocks.filter((b) => b.key !== block.key));
                }}
              >
                ×
              </button>
            </div>
          </li>
        ))}
      </ol>
      <div className={styles.addBar}>
        <Button
          size="sm"
          onClick={() => {
            onChange([...blocks, newMarkdownBlock()]);
          }}
        >
          + {t('content.addText')}
        </Button>
        <Button
          size="sm"
          onClick={() => {
            onChange([...blocks, newChecklistBlock()]);
          }}
        >
          + {t('content.addChecklist')}
        </Button>
      </div>
    </section>
  );
}
